import React from 'react';
import isNumber from 'lodash/isNumber'
import sortBy from 'lodash/sortBy'

const tooltipContent = {
  'Particulates': {
    0: 'This measures fine, inhalable particulates of 2.5 microns or less in the air. Common sources are power plants and gas emissions. High exposure is associated with elevated risk of premature mortality from cardiovascular diseases or lung cancer, and increased health problems such as asthma attacks.',
    1: 'The value reflects the concentration of particulates per cubic meter. In 2017 the Southwest U.S. average was 7.32 and the national average was 8.02.'
  },
  'Ozone': {
    0: 'This measures the summer seasonal average daily maximum 8-hour concentration of ozone in the air. Ozone is created by a chemical reaction between oxides of nitrogen (NOx) and volatile organic compounds (VOCs) in the presence of sunlight. Ozone is the primary component of smog. There is an association between exposure to ambient ozone and a variety of health outcomes, including reduction in lung function, increased inflammation and higher hospital admissions and mortality.',
    1: 'A value less than or equal to 1 indicates that the exposure is not likely to result in adverse effects. A value greater than 1 indicates that a potential exists for adverse health effects.'
  },
  'Proximity to Heavy Traffic': {
    0: 'This reflects a count of vehicles at major roads within 500 meters, divided by the distance in meters to the center of the census area.  A census block near the intersection of urban freeways (e.g., ‘the stack’ in central Phoenix) will have a very high value while a rural location would be very low.',
    1: 'Residential proximity to traffic has been associated with various health impacts, particularly asthma exacerbation and possibly onset of asthma, as well as mortality rates. Proximity to traffic has also been associated with cardiovascular disease, coronary heart disease, and incidence of myocardial infarction.'
  },
  'Lead Paint': {
    0: 'The percent of housing units built pre-1960 is used as an indicator of potential lead paint exposure. A key source of exposure to lead for many Americans is through lead paint and lead-containing dust that accumulates in homes or in other buildings where lead paint was used. Children can experience neurological damage even at low levels of exposure to lead.',
    1: 'The indicator reflects a combination of the percent of housing built before 1960 and the percent of susceptible people compared to the U.S. average. A positive number indicates more older housing and/or susceptible people and a negative number indicates newer housing and/or a less susceptible population.'
  },
  'Violent Crime Index': {
    0: 'The data represent an index of Part I Violent Crimes as compiled by the FBI. Crimes include murder, rape, robbery, theft and aggravated assault. Neighborhood violent crimes have been found to disrupt children’s sleep, increase the stress hormone cortisol, decrease the ability to learn, and lower performance on academic tasks including tests.',
    1: 'The numbers reflect the distance from the national average of 100. So, 50 is half the national average, 200 is twice the national average, 400 is four times the national average, etc.'
  },
  'Exposure to Violence': {
    0: 'The data represent an index of Part I Violent Crimes as compiled by the FBI. Crimes include murder, rape, robbery, theft and aggravated assault. Neighborhood violent crimes have been found to disrupt children’s sleep, increase the stress hormone cortisol, decrease the ability to learn, and lower performance on academic tasks including tests.',
    1: 'The numbers reflect the distance from the national average of 100. So, 50 is half the national average, 200 is twice the national average, 400 is four times the national average, etc.'
  }
}

export const INFINITE = 9999999999

const color1 = '#17165e' // blue
const color2 = '#0c4076'
const color3 = '#007682'
const color4 = '#00916f'
const color5 = '#00a04f'
const color6 = '#3bae3f'
const color7 = '#8abb4f'
const color8 = '#c6c36b'


const COLOR_RANGE_MAP = {
  CANCER: {
    [color1]: { low: 52.9, high: 87, hEq: true },
    [color2]: { low: 49.79, high: 52.9, hEq: true },
    [color3]: { low: 47.6, high: 49.79, hEq: true },
    [color4]: { low: 45.5, high: 47.6, hEq: true },
    [color5]: { low: 42.9, high: 45.5, hEq: true },
    [color6]: { low: 39.5, high: 42.9, hEq: true },
    [color7]: { low: 29.9, high: 39.5, hEq: true },
    [color8]: { low: 0, high: 29.9, lEq: true, hEq: true },
  },
  D_LDPNT: {
    [color1]: { low: 77, high: 713, hEq: true },
    [color2]: { low: 22, high: 77, hEq: true },
    [color3]: { low: 3.6, high: 22, hEq: true },
    [color4]: { low: 0, high: 3.6, hEq: true },
    [color5]: { low: 0, lEq: true, hEq: true },
    [color6]: { low: -1.61, high: 0, hEq: false },
    [color7]: { low: -6, high: -1.61, hEq: true },
    [color8]: { low: -216, high: -6, hEq: true },
  },
  D_LDPNT_2: {
    [color1]: { low: 77, high: 713, hEq: true },
    [color2]: { low: 22, high: 77, hEq: true },
    [color3]: { low: 3.6, high: 22, hEq: true },
    [color4]: { low: 0, high: 3.6, hEq: true },
    [color5]: { low: 0, lEq: true, hEq: true },
    [color6]: { low: -1.61, high: 0, hEq: false },
    [color7]: { low: -6, high: -1.61, hEq: true },
    [color8]: { low: -216, high: -6, hEq: true },
  },
  PM25: {
    [color1]: { low: 8.75, high: 9.37, hEq: true },
    [color2]: { low: 8.13, high: 8.75, hEq: true },
    [color3]: { low: 7.52, high: 8.13, hEq: true },
    [color4]: { low: 6.9, high: 7.52, hEq: true },
    [color5]: { low: 6.28, high: 6.9, hEq: true },
    [color6]: { low: 5.66, high: 6.28, hEq: true },
    [color7]: { low: 5.04, high: 5.66, hEq: true },
    [color8]: { low: 4.4, high: 5.04, hEq: true },
  },
  PTRAF: {
    [color1]: { low: 2547, high: 17028, hEq: true },
    [color2]: { low: 941, high: 2547, hEq: true },
    [color3]: { low: 221, high: 941, hEq: true },
    [color4]: { low: 88, high: 221, hEq: true },
    [color5]: { low: 43, high: 88, hEq: true },
    [color6]: { low: 15.9, high: 43, hEq: true },
    [color7]: { low: 4.8, high: 15.9, hEq: true },
    [color8]: { low: 0, high: 4.8, lEq: true, hEq: true },
  },
  RESP: {
    [color1]: { low: 2.34, high: 8.9, hEq: true },
    [color2]: { low: 1.85, high: 2.34, hEq: true },
    [color3]: { low: 1.64, high: 1.85, hEq: true },
    [color4]: { low: 1.44, high: 1.64, hEq: true },
    [color5]: { low: 1.24, high: 1.44, hEq: true },
    [color6]: { low: 0.998, high: 1.24, hEq: true },
    [color7]: { low: 0.71, high: 0.998, hEq: true },
    [color8]: { low: 0, high: 0.71, lEq: true, hEq: true },
  },
  TotalCrimeIndex: {
    [color3]: { low: 400, high: INFINITE, hEq: true },
    [color4]: { low: 300, high: 400, hEq: true },
    [color5]: { low: 200, high: 300, hEq: true },
    [color6]: { low: 100, high: 200, hEq: true },
    [color7]: { low: 50, high: 100, hEq: true },
    [color8]: { low: 1, high: 50, hEq: true },
  },
  PersonalCrimeIndex: {
    [color3]: { low: 400, high: INFINITE, hEq: true },
    [color4]: { low: 300, high: 400, hEq: true },
    [color5]: { low: 200, high: 300, hEq: true },
    [color6]: { low: 100, high: 200, hEq: true },
    [color7]: { low: 50, high: 100, hEq: true },
    [color8]: { low: 1, high: 50, hEq: true },
  }
}

export const getColorRangeArrayByKey = (key) => {
  const rangeMap = COLOR_RANGE_MAP[key]
  const colorsArr = Object
    .keys(rangeMap)
    .map((color) => {
      const range = rangeMap[color]
      return { color, range, ...range, high: isNumber(range.high) ? range.high : range.low }
    })
  return sortBy(colorsArr, ['low', 'high'])
}

function getColor (key, d) {
  const colorObj = COLOR_RANGE_MAP[key]
  if (!colorObj) return null
  const colors = Object.keys(colorObj)

  for (let i = 0; i < colors.length; i += 1) {
    const color = colors[i]
    const range = colorObj[color]

    if (range.high === INFINITE && d > range.low) return color // `range.low` < x < INFINITE
    if (range.lEq && range.high === INFINITE && d >= range.low) return color // `range.low` <= x < INFINITE
    if (!isNumber(range.high) && range.lEq && d === range.low) return color // all values equals to `range.low`
    if (d > range.low && d <= range.high) return color // low < x <= high
    if (range.lEq) {
      if (d >= range.low && d < range.high) return color // low <= x < high
      if (range.hEq && d >= range.low && d <= range.high) return color // low <= x <= high
    }
    if (range.hEq) {
      if (d > range.low && d <= range.high) return color // low < x <= high
      if (range.lEq && d >= range.low && d <= range.high) return color // low <= x <= high
    }
  }
}

export function setFeatureColor(feature, geojsonLayer, variable) {
  let color = null;
  var layer = geojsonLayer;
  switch (variable) {
    case 'CANCER':
      color = getColor(variable, feature.properties.CANCER);
      break;
    case 'RESP':
      color = getColor('RESP', feature.properties.RESP);
      break;
    case 'D_LDPNT':
      color = getColor('D_LDPNT', feature.properties.D_LDPNT_2);
      break;
    case 'D_LDPNT_2':
      color = getColor('D_LDPNT', feature.properties.D_LDPNT_2);
      break;
    case 'PTRAF':
      color = getColor('PTRAF', feature.properties.PTRAF);
      break;
    case 'TotalCrimeIndex':
      color = getColor('TotalCrimeIndex', feature.properties.TotalCrimeIndex);
      break;
    case 'PersonalCrimeIndex':
      color = getColor('PersonalCrimeIndex', feature.properties.PersonalCrimeIndex);
      break;
    default:
      color = getColor('PM25', feature.properties.PM25);

  }
  layer.setStyle({
    color: color,
    fillColor: color,
    weight: 1,
    fillOpacity: 0.8
  });
}

export function highlightFeature(e, geojsonLayer, layer) {
  layer = e.target || layer;
  layer.setStyle({
    weight: 1,
    color: '#C82D41',
    pane: 'shadowPane'
  });

  layer.bringToFront();
}

export function resetHighlight(e, geojsonLayer) {
  geojsonLayer.leafletElement.resetStyle(e.target);
}

export function renderTooltipContent (label) {
  if (!tooltipContent[label]) return null; 
  return (
    <div>
      <p>{tooltipContent[label][0]}</p><br/>
      <p>{tooltipContent[label][1]}</p>
    </div>
  )
}

